import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { guestService } from "@/services/guestService";
import { EarlyAccessModal } from "@/components/EarlyAccessModal";
import { AnimatedStage } from "@/components/AnimatedStage";
import { CourseBrand } from "@/components/CourseBrand";
import { DojoControls } from "@/plugins/vibeDojo/DojoControls";
import { dojo } from "@/plugins/vibeDojo/dojoAudio";
import { AnimatePresence } from "framer-motion";
import { fmtClock, fmtMinutes, estimateSeconds, courseSeconds } from "@/lib/duration";
import {
  Play, Pause, SkipBack, SkipForward, Volume2, GraduationCap,
  CheckCircle2, Loader2, List, Lock, Award,
} from "lucide-react";

export default function GuestCoursePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Duck the Dojo ambient bed while the demo course narration plays.
  useEffect(() => { dojo.setDuck(true); return () => dojo.setDuck(false); }, []);
  const audioRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [progress, setProgress] = useState(0);
  const [audioCache] = useState(() => ({}));
  const [completed, setCompleted] = useState({});
  const [course, setCourse] = useState(null);
  const [showGate, setShowGate] = useState(false);
  const [phase, setPhase] = useState("intro");
  const [dur, setDur] = useState(0);
  const [curTime, setCurTime] = useState(0);

  useEffect(() => {
    guestService.getCourse(id)
      .then(setCourse)
      .catch(() => { toast.error("Demo course not found"); navigate("/"); });
  }, [id, navigate]);

  const loadAudio = useCallback(async (i) => {
    if (audioCache[i]) return audioCache[i];
    setLoadingAudio(true);
    try {
      const audio = await guestService.sectionAudio(id, i);
      const bin = atob(audio);
      const bytes = new Uint8Array(bin.length);
      for (let j = 0; j < bin.length; j++) bytes[j] = bin.charCodeAt(j);
      const src = URL.createObjectURL(new Blob([bytes], { type: "audio/mpeg" }));
      audioCache[i] = src;
      return src;
    } catch {
      toast.error("Couldn't load narration");
      return null;
    } finally {
      setLoadingAudio(false);
    }
  }, [id, audioCache]);

  useEffect(() => () => {
    Object.values(audioCache).forEach((u) => {
      if (typeof u === "string" && u.startsWith("blob:")) URL.revokeObjectURL(u);
    });
  }, [audioCache]);

  const playSection = useCallback(async (i, autoplay = true) => {
    setIdx(i);
    setProgress(0);
    setCurTime(0);
    setDur(0);
    const src = await loadAudio(i);
    if (!src || !audioRef.current) return;
    audioRef.current.src = src;
    if (autoplay) {
      try { await audioRef.current.play(); setPlaying(true); } catch { setPlaying(false); }
    }
  }, [loadAudio]);

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (!audioRef.current.src) { await playSection(idx, true); return; }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { try { await audioRef.current.play(); setPlaying(true); } catch {} }
  };

  const onEnded = () => {
    setCompleted((c) => ({ ...c, [idx]: true }));
    if (course && idx < course.sections.length - 1) playSection(idx + 1, true);
    else { setPlaying(false); setPhase("outro"); }
  };

  const onTime = () => {
    const a = audioRef.current;
    if (a && a.duration && isFinite(a.duration)) {
      setProgress((a.currentTime / a.duration) * 100);
      setCurTime(a.currentTime);
    }
  };

  const onMeta = () => {
    const a = audioRef.current;
    if (a && isFinite(a.duration)) setDur(a.duration);
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  const section = course.sections[idx];

  return (
    <div className="min-h-screen bg-[#050505] text-[#FAFAFA]">
      <audio ref={audioRef} onEnded={onEnded} onTimeUpdate={onTime} onLoadedMetadata={onMeta} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
      <EarlyAccessModal open={showGate} onOpenChange={setShowGate} />

      <AnimatePresence>
        {phase === "intro" && <CourseBrand mode="intro" onDone={() => setPhase("course")} />}
        {phase === "outro" && <CourseBrand mode="outro" onDone={() => setPhase("course")} />}
      </AnimatePresence>

      {/* Top bar */}
      <div className="border-b border-white/10 px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold" data-testid="guest-brand-home">
          <GraduationCap className="h-5 w-5 text-primary" /> Learn Me
        </Link>
        <span className="hidden sm:inline text-xs uppercase tracking-wider text-primary font-semibold">Free demo</span>
        <button onClick={() => setShowGate(true)} className="text-sm font-semibold bg-primary text-white px-4 h-9 hover:opacity-90 transition-opacity" data-testid="guest-signup-cta">
          Sign up free
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px]">
        {/* Stage */}
        <div className="p-6 md:p-10">
          <AnimatedStage
            section={section}
            idx={idx}
            total={course.sections.length}
            progress={progress}
            playing={playing}
            loadingAudio={loadingAudio}
          />

          {/* Controls */}
          <div className="mt-4">
            <div className="h-1.5 bg-white/10 w-full cursor-pointer" onClick={(e) => {
              const a = audioRef.current; if (!a || !a.duration) return;
              const rect = e.currentTarget.getBoundingClientRect();
              a.currentTime = ((e.clientX - rect.left) / rect.width) * a.duration;
            }} data-testid="progress-bar">
              <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>

            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-3">
                <button onClick={() => idx > 0 && playSection(idx - 1, true)} disabled={idx === 0}
                  className="h-10 w-10 flex items-center justify-center border border-white/15 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid="prev-button">
                  <SkipBack className="h-4 w-4" />
                </button>
                <button onClick={togglePlay} className="h-12 w-12 flex items-center justify-center bg-primary hover:opacity-90 transition-opacity" data-testid="play-pause-button">
                  {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                </button>
                <button onClick={() => idx < course.sections.length - 1 && playSection(idx + 1, true)} disabled={idx === course.sections.length - 1}
                  className="h-10 w-10 flex items-center justify-center border border-white/15 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid="next-button">
                  <SkipForward className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-2 text-white/50 ml-2"><Volume2 className="h-4 w-4" /></div>
              </div>
              <div className="text-sm text-white/60">{course.title}</div>
            </div>

            {/* Locked guest actions */}
            <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="guest-actions-bar">
              <button onClick={() => setShowGate(true)}
                className="inline-flex items-center gap-2 border border-white/25 text-white/80 px-4 h-10 hover:border-primary hover:text-white transition-colors text-sm font-semibold" data-testid="locked-save-button">
                <Lock className="h-4 w-4 text-primary" /> Save to Library
              </button>
            </div>
            <p className="mt-4 text-sm text-white/60 inline-flex items-center gap-2" data-testid="certificate-hint">
              <Award className="h-4 w-4 text-primary" /> Master the full training to earn your certificate.
            </p>
            <p className="mt-2 text-sm text-white/50" data-testid="guest-unlock-hint">
              <button onClick={() => setShowGate(true)} className="text-primary font-semibold hover:underline">Create a free account</button> to save this course to your library.
            </p>
          </div>

          {/* Transcript */}
          <div className="mt-8 border-t border-white/10 pt-6">
            <p className="text-xs uppercase tracking-[0.2em] text-white/40 font-semibold mb-3">Transcript</p>
            <p className="text-white/70 leading-relaxed" data-testid="transcript">{section.narration}</p>
          </div>
        </div>

        {/* Chapters sidebar */}
        <aside className="border-l border-white/10 p-6 lg:h-[calc(100vh-4rem)] lg:overflow-y-auto" data-testid="chapter-list">
          <div className="mb-6"><DojoControls /></div>
          <div className="flex items-center gap-2 mb-1"><List className="h-4 w-4 text-primary" /><h3 className="font-display font-bold">Chapters</h3></div>
          <p className="text-xs text-white/40 mb-5">{course.level} · {course.sections.length} chapters</p>
          <div className="space-y-1">
            {course.sections.map((s, i) => (
              <button key={i} onClick={() => playSection(i, true)}
                className={`w-full text-left p-3 flex items-start gap-3 transition-colors ${i === idx ? "bg-primary/15 border-l-2 border-primary" : "hover:bg-white/5 border-l-2 border-transparent"}`}
                data-testid={`chapter-${i}`}>
                <span className="text-xs font-mono text-white/40 mt-0.5 w-5">{String(i + 1).padStart(2, "0")}</span>
                <span className={`text-sm leading-snug flex-1 ${i === idx ? "text-white font-semibold" : "text-white/70"}`}>{s.title}</span>
                {completed[i] && <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />}
              </button>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
